'use client';

import React, { useState } from 'react';
import { Tooltip } from '@heroui/react';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareCampaign } from '../types';
import { GroupUserIc, MoneyIc, UserCheckIc, ChatRoundedIc } from '../../../../components/icons';
import { Icon } from '@iconify/react';
import {
  calculateArpu,
  calculateArpd,
  calculateArpuWeek,
  calculateArpuMonth,
  calculateArpuYear,
  calculateArpuTotal,
} from '../utils/arpuCalculations';
import CampaignStatsModal from './CampaignStatsModal';
import { ResponsiveTooltip } from '../../../../components';

interface StatisticsCellProps {
  campaign: RevshareCampaign;
}

const StatisticsCell: React.FC<StatisticsCellProps> = ({ campaign }) => {
  const { t } = useTranslations();
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

  const handleOpenStatsModal = () => {
    setIsStatsModalOpen(true);
  };

  const handleCloseStatsModal = () => {
    setIsStatsModalOpen(false);
  };

  const formatCurrency = (value: number | string) => {
    const num = Number(value) || 0;
    return `$${num.toFixed(3)}`;
  };

  // Get statistics from API response
  const guests = campaign.model?.transitions_count || 0;
  const subscribers = campaign.subscribed_count || 0;
  const chats = campaign.subs?.with_gte_3_chat_messages_count || 0;
  const transactionsTotal = Number(campaign.transactions_statistic?.total || 0);

  // Check if campaign is active and has data
  const isActive = campaign.status === 'in_progress';
  const hasData = subscribers > 0 || transactionsTotal > 0;

  // Calculate all ARPU metrics
  const arpu = calculateArpu(transactionsTotal, subscribers);
  const arpd = calculateArpd(transactionsTotal, subscribers, campaign.created_at);
  const arpuWeek = calculateArpuWeek(transactionsTotal, subscribers, campaign.created_at);
  const arpuMonth = calculateArpuMonth(transactionsTotal, subscribers, campaign.created_at);
  const arpuYear = calculateArpuYear(transactionsTotal, subscribers, campaign.created_at);
  const arpuTotal = calculateArpuTotal(transactionsTotal, subscribers);

  // If campaign is not active or has no data, show simplified view
  if (!isActive || !hasData) {
    return (
      <>
        <div
          className="flex flex-col gap-1 py-2 cursor-pointer hover:bg-gray-50 rounded-md transition-colors"
          onClick={handleOpenStatsModal}
        >
          {/* Row 1: Guests, Subscribers (or just money if no subs), Total Revenue */}
          <div className="flex items-center gap-2">
            {/* Guests */}
            <ResponsiveTooltip
              content={t('partner.partnerCampaigns.statistics.guests') || 'Гости'}
              placement="top"
            >
              <div className="flex items-center gap-1 cursor-pointer">
                <GroupUserIc className="w-3 h-3" />
                <span className="text-xs text-auth-muted leading-4">{guests}</span>
              </div>
            </ResponsiveTooltip>

            {/* Total Revenue */}
            <ResponsiveTooltip
              content={t('partner.partnerCampaigns.statistics.totalRevenue') || 'Всего поступлений'}
              placement="top"
            >
              <div className="flex items-center gap-1 cursor-pointer">
                <MoneyIc className="w-3 h-3" />
                <span className="text-xs text-auth-muted leading-4">{subscribers}</span>
              </div>
            </ResponsiveTooltip>

            {/* Chart icon */}
            <div className="flex items-center">
              <Icon
                icon="solar:chart-square-linear"
                width={12}
                height={12}
                className="text-auth-muted"
              />
            </div>
          </div>

          {/* SFS posts */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-auth-muted leading-4">SFS posts:</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {t('partner.revshare.campaigns.sfsHigh') || 'High'}
            </span>
          </div>
        </div>
        <CampaignStatsModal
          isOpen={isStatsModalOpen}
          onClose={handleCloseStatsModal}
          campaign={campaign}
        />
      </>
    );
  }

  // Active campaign with data - show full statistics
  return (
    <>
      <div
        className="flex flex-col gap-1 py-2 cursor-pointer hover:bg-gray-50 rounded-md transition-colors"
        onClick={handleOpenStatsModal}
      >
        {/* Row 1: Guests, Subscribers, Chats, Total Revenue */}
        <div className="flex items-center gap-2">
          {/* Guests */}
          <ResponsiveTooltip
            content={t('partner.partnerCampaigns.statistics.guests') || 'Гости'}
            placement="top"
          >
            <div className="flex items-center gap-1 cursor-pointer">
              <GroupUserIc className="w-3 h-3" />
              <span className="text-xs text-auth-ink leading-4">{guests}</span>
            </div>
          </ResponsiveTooltip>

          {/* Subscribers */}
          <ResponsiveTooltip
            content={t('partner.partnerCampaigns.statistics.subscribers') || 'Подписчики'}
            placement="top"
          >
            <div className="flex items-center gap-1 cursor-pointer">
              <UserCheckIc className="w-3 h-3" />
              <span className="text-xs text-auth-ink leading-4">{subscribers}</span>
            </div>
          </ResponsiveTooltip>

          {/* Chats */}
          <ResponsiveTooltip
            content={t('partner.partnerCampaigns.statistics.chats') || 'Чатов'}
            placement="top"
          >
            <div className="flex items-center gap-1 cursor-pointer">
              <ChatRoundedIc className="w-3 h-3" />
              <span className="text-xs text-auth-ink leading-4">{chats}</span>
            </div>
          </ResponsiveTooltip>

          {/* Total Revenue */}
          <ResponsiveTooltip
            content={t('partner.partnerCampaigns.statistics.totalRevenue') || 'Всего поступлений'}
            placement="top"
          >
            <div className="flex items-center gap-1 cursor-pointer">
              <MoneyIc className="w-3 h-3" />
              <span className="text-xs text-auth-ink leading-4">
                {formatCurrency(transactionsTotal)}
              </span>
            </div>
          </ResponsiveTooltip>
        </div>

        {/* ARPU */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpuTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPU:</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpu)}
            </span>
          </div>
        </ResponsiveTooltip>

        {/* ARPD */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpdTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPD:</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpd)}
            </span>
          </div>
        </ResponsiveTooltip>

        {/* ARPU (w) */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpuWeekTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPU (w):</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpuWeek)}
            </span>
          </div>
        </ResponsiveTooltip>

        {/* ARPU (m) */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpuMonthTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPU (m):</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpuMonth)}
            </span>
          </div>
        </ResponsiveTooltip>

        {/* ARPU (a) */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpuYearTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPU (a):</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpuYear)}
            </span>
          </div>
        </ResponsiveTooltip>

        {/* ARPU ($) */}
        <ResponsiveTooltip content={t('partner.partnerCampaigns.arpuTotalTooltip')} placement="top">
          <div className="flex items-center flex-nowrap whitespace-nowrap gap-1">
            <span className="text-xs text-auth-ink leading-4">ARPU ($):</span>
            <span className="text-xs font-semibold text-auth-ink leading-4">
              {formatCurrency(arpuTotal)}
            </span>
          </div>
        </ResponsiveTooltip>
      </div>
      <CampaignStatsModal
        isOpen={isStatsModalOpen}
        onClose={handleCloseStatsModal}
        campaign={campaign}
      />
    </>
  );
};

export default StatisticsCell;
